'use client';

import { useMemo, useState } from 'react';
import { useRouter, useParams } from 'next/navigation';
import { toast } from 'react-toastify';
import { FiArrowRight } from 'react-icons/fi';

// Components
import CardProperty from '@src/components/atoms/card-property';
import ModalSuccess from '@src/components/molecules/modal-success';
import ModalLeads from '@src/components/molecules/modal-leads';
import SkeletonListListing from '@src/components/atoms/skeleton-list-listing';
import BadgeGolderProperty from '@src/components/molecules/homepage/badge-golden-property';

// API
import { useSubmitLeads } from '@src/apis/shared/mutation/use-post-leads';

// Context
import useGlobalCtx from '@src/context/global-ctx';
import useHomepageOfficeCtx from '@src/context/homepage-office-ctx';
import useWindowsSize from '@src/hooks/useWindowSize';

// Helper
import messageAgent from '@src/helper/whatsappLeads';
import stringToDash from '@src/helper/stringToDash';

// Assets
import IGlyph from '@public/svg/i-glyph';

const ListProperty = () => {
  const router = useRouter();
  const { isLoading, listListing } = useHomepageOfficeCtx();
  const { dd2XL } = useWindowsSize();
  const { setShowLoader, custProfile } = useGlobalCtx();
  const { mutate } = useSubmitLeads();
  const [openModal, setOpenModal] = useState('');
  const [selectedProperty, setSelectedProperty] = useState(null);
  const { companySlug } = useParams();

  const handleTanyaMarketing = (list) => {
    setOpenModal('leads');
    setSelectedProperty(list);
  };

  const onSuccess = () => {
    setOpenModal('success');
    setSelectedProperty(null);
  };

  const onClose = () => {
    setOpenModal('');
    setSelectedProperty(null);
  };

  const onSubmit = (form) => {
    setShowLoader(true);

    let payload = {
      name: form.name,
      phone: '+62' + form.phone.slice(1),
      user_id: selectedProperty?.user?.id,
      property_id: selectedProperty?.id,
      kind: 'ONE21 Portal',
      slug: selectedProperty?.slug,
    };

    mutate(payload, {
      onSuccess: () => {
        const domain =
          window.location.origin +
          `/office/${companySlug}/detail-listing/${selectedProperty?.slug}`;

        const text = `Hi, saya ingin info lebih lanjut tentang properti ini:\n\n${domain}\n\n_via ONE21 Customer Portal_`;
        messageAgent(selectedProperty?.user?.phone, text);
        setOpenModal('success');
      },
      onError: (error) => {
        toast.error(error?.response?.data?.data?.message, 'Gagal submit leads');
      },
      onSettled: () => {
        setShowLoader(false);
      },
    });
  };

  const data = useMemo(() => {
    if (!listListing?.pages?.length) return [];

    return listListing.pages.map((d) => d?.data?.data ?? []).flat();
  }, [listListing]);

  const DummyList = new Array(dd2XL ? 6 : 8).fill(null);

  const LoaderSkeleton = () => {
    return (
      <>
        {DummyList.map((_, i) => {
          return (
            <SkeletonListListing key={`skeleton-property-location-${i}`} />
          );
        })}
      </>
    );
  };

  const handleRedirect = () => {
    router.push(`/office/${companySlug}/list-listing?property_type_id=1`);
  };

  return (
    <div
      className="mt-0 mb-0 mx-auto py-[64px] w-full max-w-[1376px]
      dd3-2xl:py-[64px] dd3-2xl:max-w-[unset] dd2-2xl:px-[64px]
      dd3-xl:py-[48px] dd3-xl:px-[40px]
      dd3-lg:py-[32px] dd3-lg:px-[32px]
      dd3-md:px-[24px]"
    >
      <div
        className="mb-4 flex items-center justify-between 
        dd3-md:flex-col-reverse dd3-md:items-start dd3-md:gap-[12px]"
      >
        <p className="text-[17px]/[23.8px] tracking-[0.17px] font-satoshi">
          Properti{' '}
          <span className="font-satoshi-bold text-[#040704]">Terbaru</span>
        </p>

        <BadgeGolderProperty />
      </div>
      <div
        className="grid grid-cols-4 gap-[40px] 
       dd2-2xl:grid-cols-3 dd2-2xl:gap-x-4 
       dd2-xl:grid-cols-1 dd2-xl:gap-4"
      >
        {!!data?.length &&
          data
            .filter((_, i) => i < (dd2XL ? 6 : 8))
            .map((list, index) => {
              return (
                <CardProperty
                  href={`/detail-listing/${list.slug}`}
                  key={`list-listing-property-${index}`}
                  handleTanyaMarketing={() => handleTanyaMarketing(list)}
                  openModal={openModal}
                  setOpenModal={setOpenModal}
                  onSuccess={onSuccess}
                  onClose={onClose}
                  onSubmit={onSubmit}
                  {...list}
                />
              );
            })}
        {isLoading && LoaderSkeleton()}
      </div>

      <button
        onClick={handleRedirect}
        className="ms-auto mt-6 h-[40px] px-[12px] flex items-center justify-center rounded-xs bg-[#252526] text-[17px] leading-[23.8px] tracking-[-0.17px] text-[#FAFAFA]
         dd3-md:w-full"
      >
        Lihat Lebih Banyak
        <FiArrowRight className="text-[24px] text-[#FAFAFA]" />
      </button>

      <ModalLeads
        isOpen={openModal === 'leads'}
        onSuccess={onSuccess}
        onSubmit={onSubmit}
        onClose={onClose}
        defaultName={custProfile?.name ?? ''}
        defaultPhone={custProfile?.phone ?? ''}
      />
      <ModalSuccess
        isOpen={openModal === 'success'}
        onSuccess={onSuccess}
        onSubmit={onSubmit}
        onClose={onClose}
      />
    </div>
  );
};

export default ListProperty;
